import React, { useState, useRef, useEffect } from 'react'
import { Box, Tooltip, useTheme, Typography, IconButton } from '@material-ui/core'
import { alpha, styled } from '@material-ui/core/styles'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import ScienceIcon from '@mui/icons-material/Science'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import ViewListIcon from '@mui/icons-material/ViewList'
import BiotechIcon from '@mui/icons-material/Biotech'
import TruncatedChip from './TruncatedChip'

const ChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(1),
  width: '100%',
  height: '36px',
  overflow: 'hidden',
}))

const StyledTruncatedChip = styled(TruncatedChip)(({ theme }) => ({
  fontSize: '0.75rem',
  height: 28,
  maxWidth: '150px',
  minWidth: '60px',
  flexShrink: 0,
}))

const PlusIconChip = styled(TruncatedChip)(({ theme }) => ({
  fontSize: '0.75rem',
  height: 28,
  width: '36px',
  minWidth: '36px',
  maxWidth: '36px',
  flexShrink: 0,
  backgroundColor: alpha(theme.palette.grey[500], 0.16),
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: alpha(theme.palette.grey[500], 0.28),
  },
  '& .MuiChip-label': {
    padding: 0,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  '& .MuiChip-icon': {
    margin: 0,
    fontSize: '1rem',
  },
}))

const TooltipContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  maxWidth: '280px',
  maxHeight: '300px',
  overflow: 'auto',
}))

const TooltipSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}))

const TooltipSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
}))

const TooltipChipItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 0.75),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(0.5),
  border: `1px solid ${theme.palette.divider}`,
  fontSize: '0.75rem',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

const TooltipChipDeleteButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.25),
  marginLeft: theme.spacing(0.5),
  '& .MuiSvgIcon-root': {
    fontSize: '0.875rem',
  },
  '&:hover': {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
}))

// Unified SelectionChips component that handles all chip types
const SelectionChips = ({
  selectedHPOTerms = [],
  onRemoveHPOTerm,
  selectedDiseases = [],
  onRemoveDisease,
  selectedSamples = [],
  onRemoveSample,
  selectedPanels = [],
  onRemovePanel,
}) => {
  const theme = useTheme()
  const containerRef = useRef(null)
  const [visibleChips, setVisibleChips] = useState([])
  const [hiddenChips, setHiddenChips] = useState([])

  // Get the appropriate icon based on type
  const getIcon = (type) => {
    const iconStyle = { fontSize: '0.875rem' }
    switch (type) {
      case 'hpo':
        return <LocalHospitalIcon style={iconStyle} />
      case 'disease':
        return <ScienceIcon style={iconStyle} />
      case 'samples':
        return <ViewListIcon style={iconStyle} />
      case 'panels':
        return <BiotechIcon style={iconStyle} />
      default:
        return <ScienceIcon style={iconStyle} />
    }
  }

  // Normalize all items into a unified format
  const normalizeItems = () => {
    const allItems = []

    // Add HPO terms
    selectedHPOTerms.forEach((item) => {
      allItems.push({
        id: `hpo-${item.hpo_id}`,
        label: item.name,
        type: 'hpo',
        originalItem: item,
        onRemove: onRemoveHPOTerm,
      })
    })

    // Add diseases
    selectedDiseases.forEach((item) => {
      allItems.push({
        id: `disease-${item.key}`,
        label: item.disease,
        type: 'disease',
        originalItem: item,
        onRemove: onRemoveDisease,
      })
    })

    // Add samples
    selectedSamples.forEach((item) => {
      allItems.push({
        id: `sample-${item.value || item.id}`,
        label: item.name,
        type: 'samples',
        originalItem: item,
        onRemove: onRemoveSample,
      })
    })

    // Add panels
    selectedPanels.forEach((item) => {
      allItems.push({
        id: `panel-${item.value}`,
        label: item.name,
        type: 'panels',
        originalItem: item,
        onRemove: onRemovePanel,
      })
    })

    return allItems
  }

  // Calculate visible and hidden chips for single row
  useEffect(() => {
    const allItems = normalizeItems()

    if (allItems.length === 0) {
      setVisibleChips([])
      setHiddenChips([])
      return
    }

    const calculateLayout = () => {
      const containerWidth = containerRef.current?.offsetWidth || 600
      const chipWidth = 150 + 4 // max chip width + gap
      const plusIconWidth = 36 + 4 // plus icon width + gap
      const availableWidth = containerWidth - 8 // Account for container padding

      // Calculate how many chips can fit in one row
      const maxChipsWithoutPlus = Math.floor(availableWidth / chipWidth)
      const maxChipsWithPlus = Math.floor((availableWidth - plusIconWidth) / chipWidth)

      let visible = []
      let hidden = []

      if (allItems.length <= maxChipsWithoutPlus) {
        // All chips fit without plus icon
        visible = [...allItems]
      } else {
        // Need plus icon, so use reduced capacity
        visible = allItems.slice(0, maxChipsWithPlus)
        hidden = allItems.slice(maxChipsWithPlus)
      }

      setVisibleChips(visible)
      setHiddenChips(hidden)
    }

    // Initial calculation
    calculateLayout()

    // Recalculate on window resize
    const handleResize = () => {
      calculateLayout()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [selectedHPOTerms, selectedDiseases, selectedSamples, selectedPanels])

  // Check if we have any items at all
  const allItems = normalizeItems()
  if (allItems.length === 0) return null

  const handleRemoveChip = (chipItem) => {
    if (chipItem.onRemove) {
      chipItem.onRemove(chipItem.originalItem)
    }
  }

  // Group hidden chips by type for better tooltip organization
  const groupHiddenChipsByType = () => {
    const groups = {
      hpo: [],
      disease: [],
      samples: [],
      panels: [],
    }

    hiddenChips.forEach((chip) => {
      if (groups[chip.type]) {
        groups[chip.type].push(chip)
      }
    })

    return groups
  }

  // Create tooltip content with chips grouped by type
  const tooltipContent =
    hiddenChips.length > 0 ? (
      <TooltipContent theme={theme}>
        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5 }}>
          {hiddenChips.length} more items:
        </Typography>

        {Object.entries(groupHiddenChipsByType()).map(([type, chips]) => {
          if (chips.length === 0) return null

          const typeLabels = {
            hpo: 'HPO Terms',
            disease: 'Diseases',
            samples: 'Samples',
            panels: 'Panels',
          }

          return (
            <TooltipSection key={type} theme={theme}>
              <TooltipSectionTitle theme={theme}>
                {typeLabels[type]} ({chips.length}):
              </TooltipSectionTitle>
              {chips.map((chip) => (
                <TooltipChipItem key={chip.id} theme={theme}>
                  {getIcon(type)}
                  <Typography variant="caption" sx={{ flex: 1 }}>
                    {chip.label}
                  </Typography>
                  <TooltipChipDeleteButton
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveChip(chip)
                    }}
                    size="small"
                    aria-label="delete"
                    theme={theme}
                  >
                    <CloseIcon fontSize="inherit" />
                  </TooltipChipDeleteButton>
                </TooltipChipItem>
              ))}
            </TooltipSection>
          )
        })}
      </TooltipContent>
    ) : null

  return (
    <ChipContainer ref={containerRef} theme={theme}>
      {visibleChips.map((chip) => (
        <Tooltip key={chip.id} title={chip.label} arrow>
          <StyledTruncatedChip
            size="small"
            label={chip.label}
            color="primary"
            variant="outlined"
            onDelete={() => handleRemoveChip(chip)}
            deleteIcon={<CloseIcon fontSize="small" />}
            icon={getIcon(chip.type)}
            theme={theme}
          />
        </Tooltip>
      ))}
      {hiddenChips.length > 0 && (
        <Tooltip
          title={tooltipContent}
          arrow
          interactive
          placement="bottom-start"
          PopperProps={{
            sx: {
              '& .MuiTooltip-tooltip': {
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[8],
                padding: theme.spacing(1),
                maxWidth: 'none',
              },
            },
          }}
        >
          <PlusIconChip size="small" label={`+${hiddenChips.length}`} variant="outlined" theme={theme} />
        </Tooltip>
      )}
    </ChipContainer>
  )
}

export default SelectionChips
